import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { getEcho } from '@/lib/echo';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/toast';
import { formatDate } from '@/lib/utils';
import type { Conversation, ConversationMessage, PaginatedResponse } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Send } from 'lucide-react';
import { ProfileLink } from '@/components/ProfileLink';

export default function MessagesPage() {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeConvoId = searchParams.get('c') ? Number(searchParams.get('c')) : null;
    const [messageText, setMessageText] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const { data: convosData } = useQuery({
        queryKey: ['conversations'],
        queryFn: () => api.get<PaginatedResponse<Conversation>>('/conversations'),
    });

    const { data: threadData } = useQuery({
        queryKey: ['conversation-thread', activeConvoId],
        queryFn: () => api.get<{
            conversation: Conversation;
            messages: ConversationMessage[];
        }>(`/conversations/${activeConvoId}`),
        enabled: activeConvoId !== null,
    });

    const sendMutation = useMutation({
        mutationFn: (body: string) =>
            api.post<{ data: ConversationMessage }>(`/conversations/${activeConvoId}/messages`, { body }),
        onSuccess: () => {
            setMessageText('');
            queryClient.invalidateQueries({ queryKey: ['conversation-thread', activeConvoId] });
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            queryClient.invalidateQueries({ queryKey: ['unread-count'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    // Real-time: listen for new messages on the active conversation
    useEffect(() => {
        if (!activeConvoId) return;

        const echo = getEcho();
        const channel = echo.private(`conversation.${activeConvoId}`);

        channel.listen('.App\\Events\\MessageSent', () => {
            queryClient.invalidateQueries({ queryKey: ['conversation-thread', activeConvoId] });
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            queryClient.invalidateQueries({ queryKey: ['unread-count'] });
        });

        return () => {
            echo.leave(`conversation.${activeConvoId}`);
        };
    }, [activeConvoId, queryClient]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [threadData]);

    const conversations = convosData?.data ?? [];
    const messages = [...(threadData?.messages ?? [])].reverse();
    const activeConvo = threadData?.conversation;

    const openConvo = (id: number) => setSearchParams({ c: String(id) });
    const goBack = () => setSearchParams({});

    const handleSend = (e: React.FormEvent) => {
        e.preventDefault();
        if (messageText.trim()) sendMutation.mutate(messageText.trim());
    };

    // Mobile: show either list or thread
    // Desktop: both side by side
    return (
        <div className="flex h-[calc(100dvh-12rem)] min-h-0 gap-4">
            {/* Conversation list */}
            <div className={`w-full shrink-0 space-y-1 overflow-y-auto md:w-80 ${activeConvoId ? 'hidden md:block' : ''}`}>
                <h1 className="mb-3 text-lg font-semibold">Messages</h1>
                {conversations.length === 0 ? (
                    <p className="text-sm text-slate-400">No conversations yet.</p>
                ) : (
                    conversations.map((convo) => (
                        <button
                            key={convo.id}
                            onClick={() => openConvo(convo.id)}
                            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition hover:bg-slate-800/50 ${
                                activeConvoId === convo.id ? 'bg-slate-800/70' : ''
                            }`}
                        >
                            {convo.other_user.avatar_path ? (
                                <img src={convo.other_user.avatar_path} alt="" className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-slate-400">
                                    {convo.other_user.display_name[0]?.toUpperCase()}
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium truncate">{convo.other_user.display_name}</span>
                                    {convo.last_message_at && (
                                        <span className="text-[10px] text-slate-500 shrink-0 ml-2">
                                            {formatDate(convo.last_message_at)}
                                        </span>
                                    )}
                                </div>
                                {convo.last_message && (
                                    <p className="text-xs text-slate-400 truncate">
                                        {convo.last_message.sender_id === user?.id ? 'You: ' : ''}
                                        {convo.last_message.body}
                                    </p>
                                )}
                            </div>
                            {convo.unread_count > 0 && (
                                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-violet-600 px-1.5 text-[10px] font-bold text-white">
                                    {convo.unread_count}
                                </span>
                            )}
                        </button>
                    ))
                )}
            </div>

            {/* Thread view */}
            <div className={`flex flex-1 flex-col ${!activeConvoId ? 'hidden md:flex' : ''}`}>
                {activeConvoId && activeConvo ? (
                    <>
                        {/* Thread header */}
                        <div className="flex items-center gap-3 border-b border-slate-800 pb-3 mb-3">
                            <button onClick={goBack} className="md:hidden text-slate-400 hover:text-slate-200">
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            {activeConvo.other_user.avatar_path ? (
                                <img src={activeConvo.other_user.avatar_path} alt="" className="h-8 w-8 rounded-full object-cover" />
                            ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-slate-400">
                                    {activeConvo.other_user.display_name[0]?.toUpperCase()}
                                </div>
                            )}
                            <ProfileLink
                                userId={activeConvo.other_user.id}
                                displayName={activeConvo.other_user.display_name}
                                className="text-sm font-semibold hover:underline"
                            />
                        </div>

                        {/* Messages */}
                        <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pb-2">
                            {messages.map((msg) => {
                                const isMine = msg.sender_id === user?.id;
                                return (
                                    <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                                        <div
                                            className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
                                                isMine
                                                    ? 'bg-violet-600 text-white'
                                                    : 'bg-slate-800 text-slate-100'
                                            }`}
                                        >
                                            <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                                            <p className={`mt-1 text-[10px] ${isMine ? 'text-violet-200' : 'text-slate-500'}`}>
                                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input */}
                        <form onSubmit={handleSend} className="flex gap-2 border-t border-slate-800 pt-3">
                            <Input
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                placeholder="Type a message..."
                                maxLength={2000}
                                className="flex-1"
                                autoFocus
                            />
                            <Button type="submit" disabled={!messageText.trim() || sendMutation.isPending} size="sm">
                                <Send className="h-4 w-4" />
                            </Button>
                        </form>
                    </>
                ) : (
                    <div className="flex flex-1 items-center justify-center">
                        <p className="text-sm text-slate-500">Select a conversation to start chatting.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
